import * as THREE from "three";
import { LANE_X, LaneIndex } from "./Lanes";
import { CHUNK_LENGTH, RoadChunk } from "./Road";
import { Spawnable, SpawnableKind, createSpawnable } from "./Spawnable";

const ROWS_PER_CHUNK = 6;
const ROW_SPACING = CHUNK_LENGTH / ROWS_PER_CHUNK;

interface ChunkPattern {
  rows: (SpawnableKind | null)[][];
}

/**
 * Pure lane-change driving patterns.
 * Every row is [left, center, right].
 * Every pattern leaves at least one clear path through lane changes only.
 */
const PATTERNS: ChunkPattern[] = [
  // Coin avenue — open road, collect a line of coins
  {
    rows: [
      [null,   "coin", null  ],
      [null,   "coin", null  ],
      ["coin", null,   null  ],
      ["coin", null,   "coin"],
      [null,   null,   "coin"],
      [null,   "coin", null  ],
    ],
  },
  // Solo cruiser — one slow car, easy dodge
  {
    rows: [
      [null,   null,   null  ],
      [null,   "car",  null  ],
      ["coin", null,   "coin"],
      [null,   null,   null  ],
      [null,   null,   "car" ],
      ["coin", "coin", null  ],
    ],
  },
  // Outer lane traffic — stay center or weave
  {
    rows: [
      ["car",  null,   "car" ],
      [null,   "coin", null  ],
      [null,   "coin", null  ],
      ["car",  null,   "car" ],
      [null,   "coin", null  ],
      [null,   null,   null  ],
    ],
  },
  // Center blocker — must pick a side
  {
    rows: [
      [null,   "car",  null  ],
      ["coin", null,   "coin"],
      [null,   null,   null  ],
      [null,   "car",  null  ],
      ["coin", null,   "coin"],
      [null,   null,   null  ],
    ],
  },
  // Power-up rest stop
  {
    rows: [
      [null,   null,    null  ],
      [null,   "shield",null  ],
      ["coin", null,   "coin" ],
      ["coin", null,   "coin" ],
      [null,   "x2",    null  ],
      [null,   null,    null  ],
    ],
  },
  // Zigzag traffic — keep switching lanes
  {
    rows: [
      ["car",  null,   null  ],
      [null,   "coin", null  ],
      [null,   null,   "car" ],
      [null,   "coin", null  ],
      ["car",  null,   null  ],
      [null,   "coin", null  ],
    ],
  },
  // x2 reward run
  {
    rows: [
      [null,   "x2",   null  ],
      ["coin", "coin", "coin"],
      ["coin", null,   "coin"],
      [null,   "car",  null  ],
      ["coin", null,   "coin"],
      [null,   null,   null  ],
    ],
  },
  // Rush hour — dense traffic, multiple dodges
  {
    rows: [
      ["car",  null,   "car" ],
      [null,   "coin", null  ],
      [null,   "car",  null  ],
      ["coin", null,   null  ],
      ["car",  null,   "car" ],
      [null,   "coin", null  ],
    ],
  },
  // Split highway — staggered cars, coins in gaps
  {
    rows: [
      [null,   null,   "car" ],
      ["coin", "coin", null  ],
      ["car",  null,   null  ],
      [null,   "coin", "coin"],
      [null,   "car",  null  ],
      ["coin", null,   null  ],
    ],
  },
  // Magnet bonus
  {
    rows: [
      [null,   "magnet",null  ],
      ["coin", "coin", "coin"],
      ["coin", "coin", "coin"],
      [null,   "car",  null  ],
      ["coin", null,   "coin"],
      [null,   null,   null  ],
    ],
  },
];

// "nitro" intentionally excluded — too easy to crash with the speed boost.
// The power-up system still understands it for legacy data, but new runs
// never spawn it.
const POWERUP_KINDS: SpawnableKind[] = ["magnet", "shield", "x2"];

export class Spawner {
  readonly group: THREE.Group;
  private pool: Spawnable[] = [];
  private active = new Map<RoadChunk, Spawnable[]>();

  constructor() {
    this.group = new THREE.Group();
  }

  private acquire(kind: SpawnableKind): Spawnable {
    for (let i = 0; i < this.pool.length; i++) {
      if (!this.pool[i].active && this.pool[i].kind === kind) {
        const s = this.pool.splice(i, 1)[0];
        s.active = true;
        s.mesh.visible = true;
        return s;
      }
    }
    const s = createSpawnable(kind);
    s.active = true;
    s.mesh.visible = true;
    this.group.add(s.mesh);
    return s;
  }

  private release(s: Spawnable): void {
    s.active = false;
    s.mesh.visible = false;
    this.pool.push(s);
  }

  resetAll(): void {
    for (const list of this.active.values()) {
      for (const s of list) this.release(s);
    }
    this.active.clear();
  }

  populate(chunk: RoadChunk, difficulty: number, allowCars = true): void {
    const existing = this.active.get(chunk);
    if (existing) {
      for (const s of existing) this.release(s);
      existing.length = 0;
    }
    const list = existing ?? [];

    // Easy patterns only at low difficulty
    const easyCount = 4;
    const idx =
      difficulty < 0.3
        ? Math.floor(Math.random() * easyCount)
        : Math.floor(Math.random() * PATTERNS.length);
    const pattern = PATTERNS[idx];

    const upgradeChance = Math.min(0.08, 0.02 + difficulty * 0.06);

    for (let row = 0; row < pattern.rows.length; row++) {
      for (let lane = 0; lane < 3; lane++) {
        let kind = pattern.rows[row][lane];
        if (!kind) continue;
        // First 50m grace period — skip car obstacles entirely so the
        // player has time to read the lanes before traffic arrives.
        if (kind === "car" && !allowCars) continue;
        if (kind === "coin" && Math.random() < upgradeChance) {
          kind = POWERUP_KINDS[Math.floor(Math.random() * POWERUP_KINDS.length)];
        }
        const s = this.acquire(kind);
        const localZ = -row * ROW_SPACING - ROW_SPACING / 2;
        s.mesh.position.set(LANE_X[lane as LaneIndex], 0, chunk.zRear + localZ);
        list.push(s);
      }
    }
    this.active.set(chunk, list);
  }

  forEachActive(fn: (s: Spawnable) => void): void {
    for (const list of this.active.values()) {
      for (const s of list) if (s.active) fn(s);
    }
  }

  scrollDelta(dz: number): void {
    for (const list of this.active.values()) {
      for (const s of list) s.mesh.position.z += dz;
    }
    // Animate pickups
    for (const list of this.active.values()) {
      for (const s of list) {
        if (s.kind === "coin") s.mesh.rotation.x += 0.12;
        else if (s.kind === "x2" || s.kind === "magnet" || s.kind === "shield" || s.kind === "nitro") {
          s.mesh.rotation.y += 0.04;
        }
      }
    }
  }

  magnetPull(playerPos: THREE.Vector3, radius: number, strength: number, dt: number): void {
    const r2 = radius * radius;
    for (const list of this.active.values()) {
      for (const s of list) {
        if (!s.active || s.kind !== "coin") continue;
        const dx = playerPos.x - s.mesh.position.x;
        const dy = playerPos.y - s.mesh.position.y;
        const dz = playerPos.z - s.mesh.position.z;
        if (dx * dx + dy * dy + dz * dz > r2) continue;
        const k = strength * dt;
        s.mesh.position.x += dx * k;
        s.mesh.position.y += dy * k;
        s.mesh.position.z += dz * k;
      }
    }
  }

  despawn(s: Spawnable): void {
    for (const [chunk, list] of this.active) {
      const i = list.indexOf(s);
      if (i >= 0) {
        list.splice(i, 1);
        this.release(s);
        if (list.length === 0) this.active.delete(chunk);
        return;
      }
    }
  }
}
